
import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { HisBackendService } from '../../core/service/backend/his-backend.service';
import { HisCacheService } from '../../core/service/backend/his-cache.service';
import { HisSearchConfiguration } from '../model/his-search-configuration';
import { HisSearchRequest, HisSortCriteria } from '../model/his-search-request';
import { HisSearchResult } from '../model/his-search-result';
import { HisSearchSelectfromService } from '../service/his-search-selectfrom.service';
import { HisSelectOptions, SearchFormDataRequest, VersionedKey } from '../model/his-select-options';
import { FilteredOptionsRequest } from '../model/filtered-options-request';
import { HisOption } from '../model/his-option';
import { HisOperatorTrigger } from '../model/his-operator-trigger';
import { HisSearch, HisSearchCriteria } from '../model/search/his-search';
import { FilteredSelectOptionsRequest } from '../model/filtered-select-options-request';
import { LoadOptionsByIdsRequest } from '../model/options/load-options-by-ids-request';
import { OptionByIdRequest } from '../model/options/option-by-id-request';
import { SelectedOptions } from '../model/options/selected-options';

@Injectable({providedIn: 'root'})
export class HisSearchService {
    private hisBackendService = inject( HisBackendService );
    private hisCacheService = inject( HisCacheService );
    private hisSearchSelectfromService = inject( HisSearchSelectfromService );


    public resetInputs = new BehaviorSubject<boolean>( undefined );

    public selectOperator = new BehaviorSubject<HisOperatorTrigger>( undefined );

    // Wird in "his-page-option-panel.component" für den Zugriff auf die aktuelle Suchkonfigurationsdatei verwendet.
    // Used inside "his-page-option-panel.component" to access up to date search configuration file
    public subjectSearchConfigurationValueChanged = new BehaviorSubject<string>( undefined );

    /**
     * Retrieves a search configuration from in-memory cache or loads it from the backend.
     * The cache is very useful in case that we have many sub-searches on a page,
     * like the author sub-searches in "edit publication".
     *
     * @param configFile name of the search configuration file
     * @return Observable<HisSearchConfiguration>
     */
    public loadSearchConfiguration( configFile: string ): Observable<HisSearchConfiguration> {
        // Liefere neue Suchkonfigurationsdatei an Abonnenten aus. 
        // make available new search configuration file name to subscribers
        this.subjectSearchConfigurationValueChanged.next( configFile );
        return this.hisCacheService.getDataCachedInMemory( 'api/v1/cs/sys/genericSearch/loadSearchConfig?configFile=' + configFile );
    }

    /**
     * loads dropdown-list content for a specific search configuration from the backend
     *
     * @param searchConfig HisSearchConfiguration
     * @return map of selectFrom identifiers to Observables
     */
    // TODO we only need the config file name, not the complete search configuration
    public loadDataForSearchForm( searchConfig: HisSearchConfiguration, requiredKeys: string[] ): Map<string, Observable<HisOption[]>> {

        // let x = null;console.log( 'this is an error', x[7] );

        let result: Map<string, BehaviorSubject<HisOption[]>> = new Map();

        for ( let key of requiredKeys ) {
            let observable: BehaviorSubject<HisOption[]> = new BehaviorSubject( [] );
            result.set( key, observable );
        }

        console.log( 'loadDataForSearchForm searchConfig', searchConfig );
        console.log( 'loadDataForSearchForm requiredKeys', requiredKeys );

        if ( searchConfig.configFile === 'customOptions.xml' ) {
            return result;
        }

        let cachedSelectsObservable: Observable<Map<string, HisSelectOptions>>
            = this.hisSearchSelectfromService.getKnownSelectfromLists( requiredKeys );

        cachedSelectsObservable.subscribe( ( cachedSelects: Map<string, HisSelectOptions> ) => {

            let versionedKeys: VersionedKey[] = [];
            cachedSelects.forEach( ( value: HisSelectOptions, key: string ) => {
                versionedKeys.push( value.vkey );
            } );

            let request: SearchFormDataRequest = { configFile: searchConfig.configFile, requestedKeys: versionedKeys };
            let serverResponse: Observable<HisSelectOptions[]>
                = this.hisBackendService.postData( 'api/v1/cs/sys/genericSearch/loadSearchFormData', request );

            serverResponse.subscribe( ( optionsList: HisSelectOptions[] ) => {
                for ( let options of optionsList ) {
                    let observer = result.get( options.vkey.key );
                    let cachedOptions = cachedSelects.get( options.vkey.key );
                    if ( options.options ) {
                        // send options from server to view
                        observer.next( options.options );
                    } else {
                        observer.next( cachedOptions.options );
                    }

                    let nOptions = options.options.length;
                    let nOptionsNil = 0;
                    for ( let i = 0; i < nOptions; i++ ) {
                        let option = options.options[i];
                        if ( option && ( !option.id || option.id === 'NULL' ) ) { nOptionsNil++; }
                    }
                    console.log( 'Data for options \'' + options.vkey.key + '\' loaded ['
                        + nOptions + ' option(s), ' + nOptionsNil + ' nil option(s)].' );
                }
            } );
        } );

        return result;
    }

    /**
     * Loads filtered options for autocomplete fields. Constraints apply so far hardcoded on the
     * defaulttext column of the given search entity.
     *
     * Caching makes little sense here because there are too many possibilities for the where part.
     *
     * @param configFile searchConfiguration file
     * @param searchEntity the entity from which to select the data
     * @param whereValue: constraint on the whereColumn of that entity
     * @param limit maximum number of select options to load
     * @return Observable<HisOption[]>
     */
    public loadFilteredOptions( configFile: string, whereValue: string, limit: number ): Observable<HisOption[]> {
        let request: FilteredOptionsRequest = new FilteredOptionsRequest( configFile, whereValue, limit );
        console.log( 'HisSearchService.loadFilteredOptions(): request =', request );
        return this.hisBackendService.postData( 'api/v1/cs/sys/genericSearch/loadFilteredOptions', request );
    }

    /**
     * Loads filtered options for autocomplete fields with selectFrom-methods.
     *
     * Caching makes little sense here because there are too many possibilities for the where part.
     *
     * @param configFile searchConfiguration file
     * @param selectFrom the selectFrom method
     * @param optionFilter: filter on the received options
     * @param limit maximum number of select options to load
     * @return Observable<HisOption[]>
     */
    public loadFilteredSelectOptions( configFile: string, selectFrom: string, optionFilter: string, limit: number ): Observable<HisOption[]> {
        let request: FilteredSelectOptionsRequest = new FilteredSelectOptionsRequest( configFile, selectFrom, optionFilter, limit );
        console.log( 'HisSearchService.loadFilteredOptionsBySelect(): request =', request );
        return this.hisBackendService.postData( 'api/v1/cs/sys/genericSearch/loadFilteredOptionsBySelect', request );
    }

    /**
     * Lets the backend validate the inputs of the search form.
     *
     * @param search search constraints
     * @return resultCode as Observable:
     * 0: empty search result
     * 1: non-empty search result, grouping failed
     * 2: non-empty search result, grouping worked
     */
    public validateInputs( search: HisSearch ): Observable<number | string[]> {
        console.log( 'Validating search form inputs', search, ' for \'' + search.configFile + '\'...' );
        if ( !search.configFile ) { throw new Error( 'No search configuration file defined.' ); }

        let sortCriteria: HisSortCriteria[] = [];

        let request: HisSearchRequest = {
            search,
            offset: 0,
            limit: -1,
            sortCriteria
        };
        if (search.allResultConfigFiles?.length) {
            return this.hisBackendService.postData( 'api/v1/cs/sys/genericSearch/validateInputsMultiSearch', request );
        } else {
            return this.hisBackendService.postData( 'api/v1/cs/sys/genericSearch/validateInputs', request );
        }
    }

    /**
     * Loads a search result from the backend.
     *
     * @param search search constraints
     * @param offset the first result row to load
     * @param limit the maximum number of result rows to load
     */
    public loadSearchResult( search: HisSearch, offset: number = 0, limit: number = 10, sortCriteria?: HisSortCriteria[], tableFilter?: HisSearchCriteria[] ): Observable<HisSearchResult> {
        console.log( 'Loading search results with constraints:', search );
        if ( !search ) { throw new Error( 'Search must not be null or undefined' ); }
        if ( !search.configFile ) { throw new Error( 'No search configuration file defined.' ); }
        if ( !sortCriteria ) { sortCriteria = []; }

        console.log( 'his-search.servie#loadSearchResult() #sortCriterion', sortCriteria );

        let request: HisSearchRequest = {
            search,
            offset,
            limit,
            sortCriteria,
            tableFilter
        };
        let serverResponse: Observable<HisSearchResult>
            = this.hisBackendService.postData( 'api/v1/cs/sys/genericSearch/loadSearchResult', request ).pipe( map(
                x => x ? new HisSearchResult( x ) : null ) );

        return serverResponse;
    }

    /**
     * @param resetInputs true, if the inputs should be reseted,
     * to use for fields like the select field of search criteria sets,
     * that is not part of the form
     *
     * @returns a HisSearchService.resetInputs: BehaviorSubject<boolean>
     */
    public setResetInputs( resetInputs: boolean ) {
        this.resetInputs.next( resetInputs );
    }

    /**
     * Sets the {@link HisOperatorTrigger} while loading a search set
     *
     * @param operatorTrigger a {@link HisOperatorTrigger}
     * @returns a {@link HisSearchService.selectOperator} as a BehaviorSubject<HisOperatorTrigger>
     */
    public setOperator( operatorTrigger: HisOperatorTrigger ) {
        console.log( 'HisSearchService.setOperator: trigger=', operatorTrigger );
        this.selectOperator.next( operatorTrigger );
    }

    /**
     * Loads options by given ids.
     * @param configFile searchConfiguration file
     * @param optionByIdRequests the list of options

     * @return Observable<Map<string, HisOption>> a map, with keys
     */
    public loadOptionsByIds( configFile: string, optionByIdRequests: OptionByIdRequest[] ): Observable<SelectedOptions[]> {
        const request: LoadOptionsByIdsRequest = new LoadOptionsByIdsRequest( configFile, optionByIdRequests );
        console.log( 'HisSearchService.loadOptionsByIds(): request =', request );

        //should we cache this?
        //return this.hisCacheService.getDataCachedInMemory( 'api/v1/cs/sys/genericSearch/loadOptionsByIds' + request );

        return this.hisBackendService.postData( 'api/v1/cs/sys/genericSearch/loadOptionsByIds', request );
    }
}
